import {
  ChartLine,
  Database,
  Info,
  KeyRound,
  LayoutDashboard,
  Route,
  Settings,
} from "lucide-react";
import { Link } from "react-router";

import {
  RobotoDivider,
  RobotoStyledTab,
  RobotoStyledTabs,
} from "@/shared/components";
import { useCheckFeatureFlag } from "@/shared/domain/featureFlags/hooks/useCheckFeatureFlag";

import styles from "./DeviceTabs.module.css";
import { useActiveDeviceTab, DeviceTab } from "./useActiveDeviceTabFromUrl";

/**
 * Tab strip of the device page. Reads its active value from the URL via
 * `useActiveDeviceTab`, so it carries no device-record dependency and renders
 * before the device has loaded.
 */
export function DeviceTabs() {
  // Derived from the URL each tab's <Link> navigates to, so there is no local
  // tab state that could drift from the address bar. A tab opens on its own
  // defaults: one tab's query string (the Overview's window and filters)
  // means nothing on another.
  const activeTab = useActiveDeviceTab();
  // Show the Sessions tab only when `release-sessions` is on.
  // The same flag gates the /devices/:deviceId/sessions route in DevicesRoutes.tsx, so
  // hiding the tab here keeps the strip aligned with reachable routes.
  const sessionsTabEnabled = useCheckFeatureFlag("release-sessions");
  // Same flag gates the /devices/:deviceId/metrics route in DevicesRoutes.tsx.
  const metricsTabEnabled = useCheckFeatureFlag("release-metrics");
  // Same flag gates the /devices/:deviceId/configurations route in DevicesRoutes.tsx.
  const configurationsTabEnabled = useCheckFeatureFlag("release-asset-store");
  // Same flag gates the /devices/:deviceId/overview route in DevicesRoutes.tsx.
  const overviewTabEnabled = useCheckFeatureFlag("release-device-timeline");

  return (
    <div className={styles.tabBar}>
      <RobotoStyledTabs value={activeTab} aria-label="device tabs">
        {overviewTabEnabled && (
          <RobotoStyledTab
            value={DeviceTab.Overview}
            label="Overview"
            icon={<LayoutDashboard size="1em" className={styles.tabIcon} />}
            iconPosition="start"
            component={Link}
            to={DeviceTab.Overview}
            replace
          />
        )}
        {sessionsTabEnabled && (
          <RobotoStyledTab
            value={DeviceTab.Sessions}
            label="Sessions"
            icon={<Route size="1em" className={styles.tabIcon} />}
            iconPosition="start"
            component={Link}
            to={DeviceTab.Sessions}
            replace
          />
        )}
        <RobotoStyledTab
          value={DeviceTab.Datasets}
          label="Datasets"
          icon={<Database size="1em" className={styles.tabIcon} />}
          iconPosition="start"
          component={Link}
          to={DeviceTab.Datasets}
          replace
        />
        {configurationsTabEnabled && (
          <RobotoStyledTab
            value={DeviceTab.Configurations}
            label="Configurations"
            icon={<Settings size="1em" className={styles.tabIcon} />}
            iconPosition="start"
            component={Link}
            to={DeviceTab.Configurations}
            replace
          />
        )}
        {metricsTabEnabled && (
          <RobotoStyledTab
            value={DeviceTab.Metrics}
            label="Metrics"
            icon={<ChartLine size="1em" className={styles.tabIcon} />}
            iconPosition="start"
            component={Link}
            to={DeviceTab.Metrics}
            replace
          />
        )}
        <RobotoStyledTab
          value={DeviceTab.Details}
          label="Details"
          icon={<Info size="1em" className={styles.tabIcon} />}
          iconPosition="start"
          component={Link}
          to={DeviceTab.Details}
          replace
        />
        <RobotoStyledTab
          value={DeviceTab.Tokens}
          label="Tokens"
          icon={<KeyRound size="1em" className={styles.tabIcon} />}
          iconPosition="start"
          component={Link}
          to={DeviceTab.Tokens}
          replace
        />
      </RobotoStyledTabs>
      <RobotoDivider />
    </div>
  );
}
